import React, { useState, useRef, useEffect } from 'react';
import { Search, X, User, Users } from 'lucide-react';

export interface SearchSuggestion {
  type: 'staff' | 'client';
  name: string;
  count: number;
}

interface SearchBarProps {
  suggestions: SearchSuggestion[];
  onSearch: (query: string, type: 'staff' | 'client' | null) => void;
  onClear: () => void;
  isActive: boolean;
}

export function SearchBar({ suggestions, onSearch, onClear, isActive }: SearchBarProps) {
  const [query, setQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState<SearchSuggestion[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (query.length >= 2) {
      const filtered = suggestions
        .filter(s => s.name.toLowerCase().includes(query.toLowerCase()))
        .sort((a, b) => b.count - a.count)
        .slice(0, 8);
      setFilteredSuggestions(filtered);
      setShowSuggestions(filtered.length > 0);
    } else {
      setFilteredSuggestions([]);
      setShowSuggestions(false);
    }
  }, [query, suggestions]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (suggestion: SearchSuggestion) => {
    setQuery(suggestion.name);
    setShowSuggestions(false);
    onSearch(suggestion.name, suggestion.type);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      setShowSuggestions(false);
      onSearch(query.trim(), null);
    }
  };

  const handleClear = () => {
    setQuery('');
    setShowSuggestions(false);
    onClear();
    inputRef.current?.focus();
  };

  return (
    <div ref={containerRef} className="relative w-full sm:max-w-md">
      <form onSubmit={handleSubmit}>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => query.length >= 2 && setShowSuggestions(true)}
            placeholder="Search staff or client..."
            className={`w-full pl-10 pr-10 py-2 bg-slate-800 border rounded-lg text-sm 
                      text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-2 
                      focus:ring-blue-500 transition-colors ${
                        isActive ? 'border-blue-500' : 'border-slate-700'
                      }`}
          />
          {(query || isActive) && (
            <button
              type="button"
              onClick={handleClear}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 
                       hover:text-slate-200 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </form>

      {/* Suggestions Dropdown */}
      {showSuggestions && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-slate-800 border 
                      border-slate-700 rounded-lg shadow-xl z-50 overflow-hidden">
          {filteredSuggestions.map((suggestion, index) => (
            <button
              key={`${suggestion.type}-${suggestion.name}-${index}`}
              onClick={() => handleSelect(suggestion)}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-left 
                       hover:bg-slate-700 transition-colors"
            >
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                suggestion.type === 'staff' 
                  ? 'bg-blue-500/20 text-blue-400' 
                  : 'bg-green-500/20 text-green-400'
              }`}>
                {suggestion.type === 'staff' ? (
                  <User className="w-4 h-4" />
                ) : (
                  <Users className="w-4 h-4" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-200 truncate">
                  {suggestion.name}
                </p>
                <p className="text-xs text-slate-500">
                  {suggestion.type === 'staff' ? 'Staff' : 'Client'} • {suggestion.count} services
                </p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
